import React, { useState, useEffect, useRef } from 'react'
import { Dispatch, bindActionCreators } from 'redux'
import clsx from 'clsx'
import { Helmet } from 'react-helmet'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Fade from '@mui/material/Fade'
import Typography from '@mui/material/Typography'
import { ReduxProps } from 'utils/Types'

import { route, ROUTE_AFTER_LOGIN } from 'routes'
import SigninPanel from './SigninPanel'
import Page from 'components/Page'
import Button from 'components/Button'
import { RootState, RootActionType } from 'duck'
import { apiSelectors } from 'duck/ApiDuck'
import { appSelectors } from 'duck/AppDuck'
import { LSKey } from 'appConstants'
import { LocalStorage } from 'utils'
import { ImgPure } from 'components/Img'
import { useBreakpoint } from 'utils/Hooks'
import { firebaseSelectors } from 'duck/FirebaseDuck'
import { scrollTo } from 'utils/MiscUtils'
import TestimonialCarousel from './TestimonialCarousel'

import stylesGlobal from 'stylesGlobal.module.scss'
import styles from './Signin.module.scss'

import imgArtnet from 'assets/images/featured-media/artnet.png'
import imgFastCompany from 'assets/images/featured-media/fastcompany.png'
import imgHyperAllergic from 'assets/images/featured-media/hyperallergic.png'
import imgTheAtlantic from 'assets/images/featured-media/theatlantic.png'
// import imgTheNewYorkTimes from 'assets/images/featured-media/thenewyorktimes.png'
import imgTheWallStreetJournal from 'assets/images/featured-media/thewallstreetjournal.png'

import imgTrain from 'assets/images/signin-content-feature/train.gif'
import imgMorph from 'assets/images/signin-content-feature/morph.gif'
import imgTransfer from 'assets/images/signin-content-feature/transfer.gif'
import imgSketch from 'assets/images/signin-content-feature/sketch.gif'
import imgMix from 'assets/images/signin-content-feature/mix.gif'
import imgVideo from 'assets/images/signin-content-feature/video.gif'
import { changeEmailActions } from 'duck/AppDuck/ChangeEmailDuck'

const FeaturedMedia = [
  // {
  //   title: 'The New York Times',
  //   image: imgTheNewYorkTimes
  // },
  {
    title: 'The Wall Street Journal',
    image: imgTheWallStreetJournal
  },
  {
    title: 'Hyperallergic',
    image: imgHyperAllergic
  },
  {
    title: 'Artnet',
    image: imgArtnet
  },
  {
    title: 'Fast Company',
    image: imgFastCompany
  },
  {
    title: 'The Atlantic',
    image: imgTheAtlantic
  }
]

const Features = [
  {
    title: 'Train',
    description:
      'Train your own AI with a selection of 30+ images to generate limitless iterations. Input your selection of curated images into Playform and your AI companion will create a sequence of amalgamations that reference your original inputs.',
    image: imgTrain
  },
  {
    title: 'Morph',
    description:
      'Select two sets of images for inspirations and another for influences. Playform AI will create a sequence of images that transition between both sets, resulting in a mixture between your influences and inspirations.',
    image: imgMorph
  },
  {
    title: 'Transfer',
    description:
      'Transfer aesthetics between two series of images with your AI companion. Input both series of images and Playform AI generates stylized results that use the compositions of the first set and the style of the second.',
    image: imgTransfer
  },
  {
    title: 'Sketch',
    description:
      'Draw with your mousepad or upload your own sketch, Playform AI will transform your sketch to new styles and aesthetics. Choose from a selection of styles, or upload your own style.',
    image: imgSketch
  },
  {
    title: 'Mix',
    description:
      'Generate completely original portraits by selecting up to four faces, resulting in a progression of results from one face to the next. Alter attributes like age, smile, makeup, and more. Works for illustration and design too.',
    image: imgMix
  },
  {
    title: 'Video',
    description:
      'Create videos that show the progression of your generated results from Playform projects. Choose keyframes and let Playform AI create new intermediate frames for you. Generated videos are at 1M Pixel resolution.',
    image: imgVideo
  }
]

const mapStateToProps = (state: RootState) => ({
  user: apiSelectors.user(state),
  authorized: appSelectors.isAuthorized(state),
  isUserLoading: appSelectors.isUserLoading(state),
  isLoading: firebaseSelectors.isLoading(state),
  phoneValidationCreditText: apiSelectors.phoneValidationCreditText(state)
})

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      didMountLoginPage: changeEmailActions.didMountLoginPage
    },
    dispatch
  )

type SigninProps = ReduxProps<typeof mapStateToProps, typeof mapDispatchToProps>

const FeaturedMediaGridItem = (props: { title: string; image: any }) => {
  const { image, title } = props

  return (
    <div className={clsx('my-3 flex items-center', styles.FeaturedImageWrapper)}>
      <img
        className={clsx('pointer-events-none block max-w-full select-none', styles.FeaturedImage)}
        src={image}
        alt={title}
        title={title}
      />
    </div>
  )
}

const FeatureGridItem = (props: { description: string; title: string; image: any }) => {
  const { description, image, title } = props

  return (
    <Grid xs={12} sm={6} md={4} item>
      <div /* className={clsx(styles.FeatureGridItem)} */>
        <div className={clsx('relative', styles.FeatureGridImgWrapper)}>
          <ImgPure
            className={clsx('absolute left-0 top-0 block', stylesGlobal.BorderWhiteHi)}
            src={image}
            alt={title}
            loading="lazy"
            objectFit="cover"
            title={title}
            fullWidth
            fullHeight
          />
        </div>

        <Typography className="mt-6 font-bold" variant="subtitle1" component="div">
          {title}
        </Typography>

        <Typography className="mt-2 text-white-med-on-dark" variant="subtitle2" component="div">
          {description}
        </Typography>
      </div>
    </Grid>
  )
}

const Signin: React.FC<SigninProps> = props => {
  const {
    phoneValidationCreditText,
    authorized,
    isUserLoading,
    isLoading,
    user,
    didMountLoginPage
  } = props

  const navigate = useNavigate()
  const loginContainerRef = useRef<HTMLDivElement | null>(null)
  const pageRef = useRef<HTMLDivElement | null>(null)
  const [shouldRender, setShouldRender] = useState(false)
  const timeoutRef = useRef(0)
  const { isSmallscreenAbove, isMediumscreenAbove } = useBreakpoint()
  const isUserAuthorized = authorized && Boolean(user)
  const isUserDataIncomplete = authorized && (!(user?.first_name ?? '') || !user?.is_agree_toc)

  useEffect(() => {
    didMountLoginPage()
  }, [didMountLoginPage])

  useEffect(() => {
    if (!isUserLoading && !isLoading) {
      timeoutRef.current = window.setTimeout(() => {
        setShouldRender(true)
      }, 400)
    }

    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    }
  }, [isUserLoading, isLoading])

  useEffect(() => {
    if (isUserAuthorized) {
      const redirectUrl = LocalStorage.get(LSKey.REDIRECT_AFTER_LOGIN)
      LocalStorage.remove(LSKey.REDIRECT_AFTER_LOGIN)

      if (isUserDataIncomplete) navigate(route.WELCOME.getUrl())
      else if (redirectUrl) navigate(redirectUrl)
      else navigate(ROUTE_AFTER_LOGIN)
    }
  }, [isUserAuthorized, isUserDataIncomplete, navigate])

  return (
    <Page ref={pageRef} disableContentSpacing fullWidth>
      <Helmet>
        <title>Playform - Login</title>
      </Helmet>

      <div ref={loginContainerRef}>
        <SigninPanel />
      </div>

      <Fade in={shouldRender} timeout={400}>
        <div>
          <div className="w-full bg-light text-center">
            <div className={clsx('m-auto max-w-screen-lg', styles.SigninSectionFeatured)}>
              <Typography className="mb-8 text-white-med-on-dark" variant="caption" component="div">
                FEATURED
              </Typography>

              <div className="pointer-events-none mt-6 flex select-none flex-wrap justify-center">
                {FeaturedMedia.map((item, index) => {
                  return <FeaturedMediaGridItem key={index} title={item.title} image={item.image} />
                })}
              </div>
            </div>
          </div>

          <div className="w-full bg-light">
            <div className={clsx(clsx(styles.SigninContainer, 'pb-25 pt-9'))}>
              <Typography
                className="mb-8 text-center font-bold"
                variant={isSmallscreenAbove ? 'h3' : 'h5'}
                component="h2"
              >
                Your New Creative Toolkit,
                <br />
                Powered by AI
              </Typography>

              <Grid className="mt-9 flex flex-wrap sm:mt-15 md:mt-36" spacing={6} container>
                {Features.map((item, index) => {
                  return (
                    <FeatureGridItem
                      key={index}
                      description={item.description}
                      title={item.title}
                      image={item.image}
                    />
                  )
                })}
              </Grid>
            </div>
          </div>

          <div className="w-full text-center">
            <div className={clsx(styles.SigninContainer, 'py-17')}>
              <Typography className="mb-5 text-white-med-on-dark" variant="caption" component="div">
                ARTISTS OWN ALL COPYRIGHTS
              </Typography>

              <Typography variant="h6" component="div">
                All artwork created using Playform is the intellectual property of the artist or
                user. Your work within the platform is defaulted to private unless you explicitly
                share with the community
              </Typography>
            </div>
          </div>

          <div className="w-full bg-light text-center">
            <div className={styles.SigninSectionTestimonial}>
              <div className={!isSmallscreenAbove ? 'px-6' : undefined}>
                <Typography className="mb-2 font-bold" variant={isSmallscreenAbove ? 'h3' : 'h5'}>
                  Playform is for Creative People
                </Typography>

                <Typography
                  className="text-white-med-on-dark"
                  variant={isSmallscreenAbove ? 'h6' : 'body1'}
                >
                  Playform is a Creative Assistant who helps you throughout your creative journey
                </Typography>
              </div>

              <TestimonialCarousel
                isSmallscreenAbove={isSmallscreenAbove}
                isMediumscreenAbove={isMediumscreenAbove}
              />

              <div className={clsx(!isSmallscreenAbove && 'px-6', 'mb-9')}>
                <Typography className="mb-2 font-bold" variant={isSmallscreenAbove ? 'h3' : 'h5'}>
                  What will you create with Playform?
                </Typography>

                <Typography
                  className="text-white-med-on-dark"
                  variant={isSmallscreenAbove ? 'h6' : 'body1'}
                >
                  {`New users get ${phoneValidationCreditText} in free training credits. Claim your credits now.`}
                </Typography>
              </div>

              <div>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    scrollTo({ el: pageRef?.current, top: true })
                  }}
                >
                  Get Started Free
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Fade>
    </Page>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(Signin)
